/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";

export default function CompanySwitcher({
  label = "Company",
  kind, // "store" | "accounting_client"
  options = [],
  activeId,
  onSelect,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const activeOption = useMemo(
    () => options.find((o) => o._id === activeId),
    [options, activeId]
  );

  const activeText = useMemo(() => {
    if (!activeOption) return `Select ${label}`;
    if (kind === "store") {
      return `${activeOption.name}${activeOption.location ? ` — ${activeOption.location}` : ""}`;
    }
    // accounting_client
    return `${activeOption.clientCode ? `${activeOption.clientCode} — ` : ""}${activeOption.name}`;
  }, [activeOption, kind, label]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;

    return options.filter((o) => {
      const hay = kind === "store"
        ? `${o.name || ""} ${o.location || ""}`.toLowerCase()
        : `${o.clientCode || ""} ${o.name || ""} ${o.tin || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [q, options, kind]);

  useEffect(() => {
    if (loading || kind !== "accounting_client" || !onSelect) return;
    if (!Array.isArray(options) || options.length === 0) return;

    const activeExists = options.some((option) => option?._id === activeId);
    if (activeExists) return;

    const preferred = options.find((option) => option?.clientCode === "CL001") || options[0];
    if (preferred?._id) {
      onSelect(preferred._id);
    }
  }, [activeId, kind, loading, onSelect, options]);

  // close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // focus search when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQ("");
    }
  }, [open]);

  const handlePick = (id) => {
    setOpen(false);
    if (id && id !== activeId) onSelect?.(id);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="min-w-[260px] max-w-[520px] flex items-center justify-between gap-3 border bg-white rounded px-3 py-2 shadow-sm hover:bg-gray-50"
      >
        <div className="truncate text-sm font-medium text-gray-800">
          {loading ? "Loading..." : activeText}
        </div>
        <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full bg-white border rounded shadow-lg overflow-hidden">
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full text-sm border rounded px-2 py-2 focus:outline-none focus:ring focus:border-teal-300"
            />
          </div>

          <div className="max-h-72 overflow-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">No results</div>
            ) : (
              filtered.map((o) => {
                const isActive = o._id === activeId;
                const title =
                  kind === "store"
                    ? `${o.name}${o.location ? ` — ${o.location}` : ""}`
                    : `${o.clientCode ? `${o.clientCode} — ` : ""}${o.name}`;

                const subtitle =
                  kind === "store"
                    ? (o.location || "")
                    : (o.tin ? `TIN: ${o.tin}` : "");

                return (
                  <button
                    key={o._id}
                    type="button"
                    onClick={() => handlePick(o._id)}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${
                      isActive ? "bg-teal-50" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-800">{title}</div>
                        {subtitle ? (
                          <div className="truncate text-xs text-gray-500">{subtitle}</div>
                        ) : null}
                      </div>

                      {isActive ? (
                        <span className="text-xs px-2 py-1 rounded bg-teal-100 text-teal-800">
                          Active
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
